/**
 * Aufgaben-Repository.
 *
 * Die Verbindung zum Kalender laeuft ueber zwei Felder, die immer gemeinsam
 * gesetzt oder gemeinsam geloest werden muessen: `Task.timeBlockEventId` und
 * `CalendarEvent.sourceTaskId`. Alles, was diese Kopplung aendert, laeuft
 * deshalb in einer Transaktion ueber beide Tabellen.
 */
import { db } from '@/db/schema';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import { applyLinkRule } from '@/db/repositories/links';
import { scheduleTask } from '@/db/repositories/taskSchedule';
import type { ID, LocalDate, LocalDateTime, Task, TaskStatus } from '@/domain/types';
import { serieAbhaken } from '@/db/repositories/aufgabenSerie';
import { inDenPapierkorb, rueckgaengigMachbar } from '@/db/repositories/papierkorb';

export type TaskDraft = Pick<Task, 'title'> & Partial<Omit<Task, 'id' | 'createdAt' | 'updatedAt'>>;

/** Hoechstzahl der "Big 3" je Tag - die Grenze macht die Auswahl erst wertvoll. */
export const BIG_THREE_LIMIT = 3;

export async function createTask(draft: TaskDraft): Promise<Task> {
  const ts = nowInstant();
  const maxOrder = (await db.tasks.orderBy('order').last())?.order ?? -1;

  const task: Task = {
    id: newId(),
    areaId: draft.areaId ?? null,
    title: draft.title.trim(),
    notes: draft.notes,
    status: draft.status ?? 'inbox',
    projectId: draft.projectId,
    important: draft.important ?? false,
    urgent: draft.urgent ?? false,
    dueDate: draft.dueDate,
    plannedDate: draft.plannedDate,
    isBigThree: draft.isBigThree ?? false,
    estimateMinutes: draft.estimateMinutes,
    order: draft.order ?? maxOrder + 1,
    contexts: draft.contexts,
    createdAt: ts,
    updatedAt: ts,
  };
  if (draft.goalId && !draft.projectId) task.goalId = draft.goalId;
  if (draft.subjectId) task.subjectId = draft.subjectId;
  if (draft.examId) task.examId = draft.examId;

  // Nur mit Elternteil muss die Verknuepfungsregel laufen - die Schnellerfassung
  // (der haeufigste Weg) bleibt ein einzelner Schreibzugriff.
  if (!task.projectId && !task.goalId) {
    await db.tasks.add(task);
    return task;
  }
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.tasks.add(task);
    await applyLinkRule();
  });
  return (await db.tasks.get(task.id)) ?? task;
}

export async function updateTask(id: ID, patch: Partial<Omit<Task, 'id' | 'createdAt'>>): Promise<void> {
  if (patch.title === undefined) {
    await db.tasks.update(id, { ...patch, updatedAt: nowInstant() });
    return;
  }
  // Der Zeitblock im Kalender traegt den Titel der Aufgabe mit (F-06).
  await db.transaction('rw', db.tasks, db.events, async () => {
    await db.tasks.update(id, { ...patch, updatedAt: nowInstant() });
    const task = await db.tasks.get(id);
    if (task?.timeBlockEventId) {
      await db.events.update(task.timeBlockEventId, { title: patch.title!, updatedAt: nowInstant() });
    }
  });
}

/**
 * Erledigt-Status umschalten.
 *
 * `completedAt` wird beim Zuruecknehmen geloescht, damit "heute erledigt"
 * spaeter nicht faelschlich alte Aufgaben einschliesst.
 */
export async function toggleTaskDone(id: ID): Promise<void> {
  const task = await db.tasks.get(id);
  if (!task) return;
  const done = task.status === 'erledigt';
  // Mit Rueckgaengig-Meldung (F-45); Zeitbloecke gehoeren dazu.
  await rueckgaengigMachbar({ art: done ? 'wieder_offen' : 'erledigt', tabelle: 'tasks', zeileIds: [id] }, async () => {
    // Wiederkehrend (F-24): das Vorkommen wird erledigt, die Serie rueckt weiter.
    if (task.wiederholung && !done) {
      await serieAbhaken(id);
      return;
    }
    await db.tasks.update(id, {
      status: done ? 'naechste' : 'erledigt',
      completedAt: done ? undefined : nowInstant(),
      updatedAt: nowInstant(),
    });
  }, [db.tasks, db.events]);
}

/**
 * Aufgabe loeschen (30 Tage im Papierkorb, F-45). Ein zugehoeriger Zeitblock
 * im Kalender bleibt bestehen, verliert aber seine Verknuepfung - ein Termin,
 * den man sich in den Tag geschrieben hat, soll nicht kommentarlos
 * verschwinden.
 */
export async function deleteTask(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'tasks', zeileIds: [id] }, () => db.transaction('rw', db.tasks, db.events, async () => {
    await db.events.where('sourceTaskId').equals(id).modify({ sourceTaskId: undefined });
    // Verknuepfte Termine (F-04 d) behalten die uebrigen Aufgaben.
    await db.events.where('taskIds').equals(id).modify((event) => {
      const rest = (event.taskIds ?? []).filter((t) => t !== id);
      if (rest.length) event.taskIds = rest;
      else delete event.taskIds;
    });
    await db.tasks.delete(id);
  }));
}

export async function setEisenhower(id: ID, important: boolean, urgent: boolean): Promise<void> {
  const task = await db.tasks.get(id);
  if (!task) return;

  // Aus der Inbox heraus einzuordnen heisst zugleich: verarbeitet.
  const status: TaskStatus = task.status === 'inbox' ? 'naechste' : task.status;
  await db.tasks.update(id, { important, urgent, status, updatedAt: nowInstant() });
}

/**
 * Big-3-Markierung umschalten. Die Grenze wird hier durchgesetzt und nicht
 * nur in der Oberflaeche - sonst liesse sie sich ueber zwei Tabs aushebeln.
 */
export async function toggleBigThree(id: ID): Promise<boolean> {
  return db.transaction('rw', db.tasks, async () => {
    const task = await db.tasks.get(id);
    if (!task?.plannedDate) return false;

    if (task.isBigThree) {
      await db.tasks.update(id, { isBigThree: false, updatedAt: nowInstant() });
      return true;
    }

    const count = await db.tasks
      .where('plannedDate').equals(task.plannedDate)
      .filter((t) => Boolean(t.isBigThree))
      .count();

    if (count >= BIG_THREE_LIMIT) return false;

    await db.tasks.update(id, { isBigThree: true, updatedAt: nowInstant() });
    return true;
  });
}

/**
 * Aufgabe in den Tagesplan legen (oder wieder herausnehmen).
 *
 * Hat sie einen Zeitblock, wandert er mit auf den neuen Tag (gleiche Uhrzeit)
 * bzw. verschwindet mit dem Herausnehmen - siehe `taskSchedule.ts` (F-06).
 */
export async function planForDay(id: ID, date: LocalDate | null): Promise<void> {
  const task = await db.tasks.get(id);
  if (!task) return;
  const block = task.timeBlockEventId ? await db.events.get(task.timeBlockEventId) : undefined;
  const start = block && date ? String(block.start).slice(11, 16) : undefined;
  const minutes = block ? Math.round((Date.parse(`${block.end}Z`) - Date.parse(`${block.start}Z`)) / 60_000) : undefined;
  await scheduleTask(id, { date, start, minutes });
}

// ---------------------------------------------------------------------------
// Time-Blocking: die Bruecke zum Kalender
// ---------------------------------------------------------------------------

/**
 * Legt fuer eine Aufgabe einen Zeitblock im Kalender an.
 *
 * Beide Richtungen der Verknuepfung entstehen in derselben Transaktion. Hatte
 * die Aufgabe schon einen Block, wird dieser verschoben statt ein zweiter
 * angelegt - sonst sammeln sich Karteileichen im Kalender.
 */
export async function blockTimeForTask(
  taskId: ID,
  start: LocalDateTime,
  end: LocalDateTime,
): Promise<ID | null> {
  return db.transaction('rw', db.tasks, db.events, async () => {
    const task = await db.tasks.get(taskId);
    if (!task) return null;

    const ts = nowInstant();
    const existing = task.timeBlockEventId
      ? await db.events.get(task.timeBlockEventId)
      : undefined;

    if (existing) {
      await db.events.put({ ...existing, start, end, updatedAt: ts });
      await db.tasks.update(taskId, {
        plannedDate: start.slice(0, 10),
        status: task.status === 'erledigt' ? task.status : 'geplant',
        updatedAt: ts,
      });
      return existing.id;
    }

    const eventId = newId();
    await db.events.add({
      id: eventId,
      areaId: task.areaId,
      title: task.title,
      allDay: false,
      start,
      end,
      tz: 'Europe/Berlin',
      status: 'confirmed',
      isRecurring: 0,
      sourceTaskId: taskId,
      createdAt: ts,
      updatedAt: ts,
    });

    await db.tasks.update(taskId, {
      timeBlockEventId: eventId,
      plannedDate: start.slice(0, 10),
      status: task.status === 'erledigt' ? task.status : 'geplant',
      updatedAt: ts,
    });

    return eventId;
  });
}

/** Hebt die Verknuepfung auf, ohne den Termin zu loeschen. */
export async function unlinkTimeBlock(taskId: ID): Promise<void> {
  await db.transaction('rw', db.tasks, db.events, async () => {
    const task = await db.tasks.get(taskId);
    if (!task?.timeBlockEventId) return;

    await db.events.update(task.timeBlockEventId, { sourceTaskId: undefined });
    await db.tasks.update(taskId, { timeBlockEventId: undefined, updatedAt: nowInstant() });
  });
}
